import React from 'react';
import { Avatar, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import { gql, DocumentNode } from '@apollo/client';
import {
	InvoiceViewHistoryItemEventFragment,
	EventCategory,
	InstallmentStatus,
	InvoiceViewHistoryItemInvoiceFragment,
} from '#types';
import {
	getFormattedCurrency,
	getFormattedDateWithTimestamp,
	getUsername,
} from '../../../../lib/helpers';
import rpcShared from '@rockpapercoin/rpc-shared';
import { Icon } from '../../../../blocks/icons';
import styles from './InvoiceViewHistoryItem.module.scss';

const invoiceViewHistoryItemInvoiceOrganizationFragment = gql`
	fragment invoiceViewHistoryItemInvoiceOrganization on Organization {
		id
	}
`;

const invoiceViewHistoryItemEventFragment = gql`
	fragment invoiceViewHistoryItemEvent on Event {
		id
		category
		message
		createdAt
		contact {
			id
			firstName
			lastName
			customer {
				id
				email
				clientUser {
					id
					plannerOrgCanPayInvoices
				}
			}
		}
		plannerUser {
			id
			firstName
			lastName
		}
		paymentInstallments {
			id
			name
			status
			convenienceFee
			amountPaid
			amountDue
			gratuityPaid
			paymentDate
		}
	}
`;

const invoiceViewHistoryItemInvoiceFragment = gql`
	fragment invoiceViewHistoryItemInvoice on Invoice {
		id
		lineItems {
			id
			quantity
			rate
			taxPercentage
		}
		contact {
			id
			customer {
				id
				clientUser {
					id
					assignedPlanner {
						id
						organization {
							id
							users {
								id
								user {
									id
								}
							}
						}
					}
				}
			}
		}
		organization {
			id
			users {
				id
				user {
					id
				}
			}
		}
	}
`;

const getIconType = ( category: EventCategory ) => {
	if (
		[
			EventCategory.InstallmentPaymentRequestedByGuest,
			EventCategory.InstallmentPaymentRequestedByClient,
			EventCategory.InstallmentPaymentRequestedByPlanner,
			EventCategory.InstallmentRefundRequested,
			EventCategory.InstallmentScheduledPaymentFailed,
		].includes( category )
	) {
		return 'dollar';
	} else if (
		[
			EventCategory.InstallmentPaymentRecordedOfflineByVendor,
			EventCategory.InstallmentPaymentUpdatedOfflineByVendor,
			EventCategory.InstallmentRefundRecordedOfflineByVendor,
			EventCategory.OfflineInstallmentPaymentUnRecordedByVendor,
			EventCategory.OfflineInstallmentRefundUnRecordedByVendor,
		].includes( category )
	) {
		return 'sign-check';
	} else if (
		[ EventCategory.InstallmentPaymentFailed, EventCategory.InstallmentRefundFailed, ].includes( category )
	) {
		return 'decline';
	} else if (
		[
			EventCategory.VendorDisputeOpened,
			EventCategory.InstallmentPaymentRescheduledByFailure,
			EventCategory.InstallmentPaymentRescheduledByVendor,
		].includes( category )
	) {
		return 'invoice-due';
	} else if (
		[
			EventCategory.InvoicePaymentsScheduledByClient,
			EventCategory.InvoicePaymentsScheduledByPlanner,
			EventCategory.InvoicePaymentsDescheduledByPlanner,
			EventCategory.InvoicePaymentsDescheduledByFailure,
			EventCategory.InvoicePaymentsDescheduledByClient,
			EventCategory.InvoicePaymentsDescheduledByVendorAction,
		].includes( category )
	) {
		return 'contract-overdue';
	} else if (
		[ EventCategory.VendorDisputeWonByClient, EventCategory.VendorDisputeWonByVendor, ].includes( category )
	) {
		return 'template-invoice';
	} else if ( EventCategory.InstallmentPaymentRequestedBySchedule === category ) {
		return 'due';
	}
	throw new Error(
		'unable to attribute icon type for event category ' + category
	);
};

// handle when we haven't captured money yet and we're waiting for Stripe to get back to us via the webhook
const getPaymentInformation = (
	lineItems: NonNullable<InvoiceViewHistoryItemInvoiceFragment>['lineItems'],
	paymentInstallments: NonNullable<InvoiceViewHistoryItemEventFragment>['paymentInstallments'],
	canSeeConvenienceFee: boolean
) => {
	const totals = rpcShared.invoices.calculatePaymentTotalsToString( {
		lineItems,
		paymentInstallments: paymentInstallments ?? [],
	} );
	const amountPaid = canSeeConvenienceFee
		? totals?.totalPaid
		: totals?.totalAmountPaidLessCC;
	const someInstallmentsTrulyPending = paymentInstallments?.some(
		( paymentInstallment ) =>
			paymentInstallment.status === InstallmentStatus.Pending &&
			!paymentInstallment.paymentDate
	);
	const amountDue =
		rpcShared.invoices.calculateMultipleInstallmentsAmountDueRegardlessOfStatusToString(
			paymentInstallments
		);
	return {
		waitingForWebhook: someInstallmentsTrulyPending,
		amount: someInstallmentsTrulyPending ? amountDue : amountPaid,
	};
};

export type InvoiceViewHistoryItemProps = {
	event: InvoiceViewHistoryItemEventFragment;
	invoice?: InvoiceViewHistoryItemInvoiceFragment;
	requestingUserBaseId?: string;
};

type InvoiceViewHistoryItemPropsWithFragment =
	React.FC<InvoiceViewHistoryItemProps> & {
		fragments: {
			invoiceViewHistoryItemInvoiceOrganizationFragment: DocumentNode;
			invoiceViewHistoryItemEventFragment: DocumentNode;
			invoiceViewHistoryItemInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceViewHistoryItem: InvoiceViewHistoryItemPropsWithFragment =
	( { event, invoice, requestingUserBaseId } ) => {
		const viewingUserIsVendorMember =
			invoice?.organization?.users?.some(
				( user ) => user.user?.id === requestingUserBaseId
			) || false;
		const viewingUserIsCustomer =
			invoice && requestingUserBaseId
				? rpcShared.invoices.isCustomer( invoice, requestingUserBaseId )
				: false;
		const orgUser =
			invoice?.contact?.customer?.clientUser?.assignedPlanner?.organization?.users?.find(
				( user ) => user.user?.id === requestingUserBaseId
			);
		const viewingUserIsAPPA = invoice
			? rpcShared.invoices.isAssignedPlannerOrAdminOfCustomersPlannerOrg(
				invoice,
				orgUser
			  )
			: false;
		const viewingUserIsCustomerOrAPPA =
			viewingUserIsCustomer || viewingUserIsAPPA;

		const getItemCopy = ( event: InvoiceViewHistoryItemEventFragment ) => {
			const { category } = event;
			const installmentName =
				'paymentInstallments' in event &&
				Array.isArray( event?.paymentInstallments ) &&
				event?.paymentInstallments.length > 0
					? event.paymentInstallments.reduce( ( acc, installment, index ) => {
						const isFirst = index === 0;
						const isLast =
								!!event?.paymentInstallments &&
								index === event?.paymentInstallments.length - 1;
						const separator = isFirst
							? ''
							: !!event?.paymentInstallments &&
								  event?.paymentInstallments.length === 2
								? ' and '
								: ', ';
						const and =
								isLast &&
								!!event?.paymentInstallments &&
								event?.paymentInstallments.length > 2
									? 'and '
									: '';
						return `${ acc }${ separator }${ and } ${ installment.name }`;
					  }, '' )
					: '';
			const { amount, waitingForWebhook } = invoice?.lineItems
				? getPaymentInformation(
					invoice.lineItems,
					event?.paymentInstallments,
					viewingUserIsCustomerOrAPPA && !viewingUserIsVendorMember
				  )
				: { amount: undefined, waitingForWebhook: undefined };
			const plannerName = event.plannerUser
				? getUsername( event.plannerUser )
				: undefined;
			const nameObject = event.contact
				? rpcShared.contacts.getCustomerName( event.contact )
				: undefined;
			const customerName = nameObject ? nameObject.fullName : 'customer';

			const refunded = event?.message
				? getFormattedCurrency( event?.message )
				: null;
			if ( category === EventCategory.InstallmentPaymentUpdatedOfflineByVendor ) {
				return 'Offline payment updated';
			} else if (
				category === EventCategory.InstallmentPaymentRecordedOfflineByVendor
			) {
				return `Offline payment recorded for ${ installmentName }`;
			} else if (
				category === EventCategory.InstallmentRefundRecordedOfflineByVendor
			) {
				return `Offline refund recorded for ${ installmentName }`;
			} else if (
				category === EventCategory.OfflineInstallmentPaymentUnRecordedByVendor
			) {
				return `Offline payment unrecorded for ${ installmentName }`;
			} else if (
				category === EventCategory.OfflineInstallmentRefundUnRecordedByVendor
			) {
				return `Offline refund unrecorded for ${ installmentName }`;
			} else if (
				[ EventCategory.InstallmentPaymentFailed, EventCategory.InstallmentScheduledPaymentFailed, ].includes( category )
			) {
				return 'Unsuccessful payment attempt';
			} else if ( category === EventCategory.InstallmentRefundFailed ) {
				return 'Unsuccessful refund attempt';
			} else if ( category === EventCategory.VendorDisputeOpened ) {
				return 'Payment dispute initiated';
			} else if ( category === EventCategory.VendorDisputeWonByClient ) {
				return 'Payment dispute resolved as lost';
			} else if ( category === EventCategory.VendorDisputeWonByVendor ) {
				return 'Payment dispute resolved as won';
			} else if (
				[
					EventCategory.InvoicePaymentsDescheduledByPlanner,
					EventCategory.InvoicePaymentsDescheduledByFailure,
					EventCategory.InvoicePaymentsDescheduledByClient,
					EventCategory.InvoicePaymentsDescheduledByVendorAction,
				].includes( category )
			) {
				return 'Auto-payments turned off';
			} else if (
				[ EventCategory.InstallmentPaymentRequestedByGuest, EventCategory.InstallmentPaymentRequestedByClient, ].includes( category ) &&
				amount &&
				installmentName
			) {
				return `${ amount } ${
					waitingForWebhook ? 'pending' : 'paid'
				} for ${ installmentName }`;
			} else if (
				category === EventCategory.InstallmentPaymentRequestedByPlanner &&
				amount &&
				installmentName
			) {
				return `${ amount } ${
					waitingForWebhook ? 'pending' : 'paid'
				} by planner for ${ installmentName }`;
			} else if (
				category === EventCategory.InstallmentPaymentRequestedBySchedule &&
				amount &&
				installmentName
			) {
				return `Auto-payment of ${ amount } for ${ installmentName } ${
					waitingForWebhook ? 'pending' : 'processed'
				}`;
			} else if (
				category === EventCategory.InstallmentRefundRequested &&
				refunded
			) {
				return `Refund initiated in the amount of ${ refunded }`;
			} else if (
				category === EventCategory.InvoicePaymentsScheduledByClient &&
				customerName
			) {
				return `Auto-payments scheduled by ${ customerName }`;
			} else if (
				category === EventCategory.InvoicePaymentsScheduledByPlanner &&
				plannerName
			) {
				return `Auto-payments scheduled by ${ plannerName }`;
			} else if (
				category === EventCategory.InstallmentScheduledPaymentFailed &&
				installmentName
			) {
				return `Auto-payment for ${ installmentName } failed to process`;
			} else if (
				[ EventCategory.InstallmentPaymentRescheduledByFailure, EventCategory.InstallmentPaymentRescheduledByVendor, ].includes( category ) &&
				installmentName
			) {
				return `Auto-payment for ${ installmentName } rescheduled`;
			}
			throw new Error(
				'unable to attribute copy for event category ' + category
			);
		};

		const { createdAt } = event;
		const timeStamp = getFormattedDateWithTimestamp( createdAt );
		const iconType = getIconType( event.category );
		const copy = getItemCopy( event );

		return (
			<ListItem disableGutters>
				<ListItemAvatar>
					<Avatar className={ styles.avatar }>
						<Icon type={ iconType } className={ styles.icon } />
					</Avatar>
				</ListItemAvatar>
				<ListItemText primary={ copy } secondary={ timeStamp }></ListItemText>
			</ListItem>
		);
	};

InvoiceViewHistoryItem.fragments = {
	invoiceViewHistoryItemInvoiceOrganizationFragment,
	invoiceViewHistoryItemEventFragment,
	invoiceViewHistoryItemInvoiceFragment,
};
